import moment from "moment-timezone";
import * as Papa from "papaparse";
import { Address, Fare } from "../Models";
import { getHealthCenterFinessEventually } from "./HCAdressesMap";
import { getTransporterTSICode } from "./TransporterMap";

function parseDateAndHoursString(str?: string): string | undefined {
  if (!str || str.length < 1) {
    return undefined;
  }
  const date = moment.tz(str, "DD/MM/YYYY HH:mm", "Europe/Paris");
  if (!date.isValid) {
    return undefined;
  }
  return date.format();
}

function parseDateString(str?: string): string | undefined {
  if (!str || str.length < 1) {
    return undefined;
  }
  const date = moment.tz(str, "DD/MM/YYYY", "Europe/Paris");
  if (!date.isValid) {
    return undefined;
  }
  return date.format();
}

function mapCSVToFares(csv: any[]): Fare[] {
  let fares = [];
  for (const item of csv as any[]) {
    if (item["payer"] !== "CDEM") {
      continue;
    }

    const dep: Address = {
      address: item["departureAddress"],
      zip: item["departureZip"],
      city: item["departureCity"],
      insee: item["departureINSEE"],
      placeName: item["departurePlace"],
      service: item["departureService"],
      serviceId: item["departureUF"],
      room: item["departureRoom"],
    };

    let departureHc = item["departureHealthCenter"];
    if (!departureHc) {
      const replacement = getHealthCenterFinessEventually(dep);
      if (replacement) {
        departureHc = replacement.finess;
        dep.address = replacement.address;
      }
    }

    const arr: Address = {
      address: item["arrivalAddress"],
      zip: item["arrivalZip"],
      city: item["arrivalCity"],
      insee: item["arrivalINSEE"],
      placeName: item["arrivalPlace"],
      serviceId: item["arrivalUF"],
    };

    let arrivalHC = item["arrivalHealthCenter"];
    if (!arrivalHC) {
      const replacement = getHealthCenterFinessEventually(arr);
      if (replacement) {
        arrivalHC = replacement.finess;
        arr.address = replacement.address;
      }
    }

    const fare: Fare = {
      identifier: item["identifier"],
      ordererId: item["orderer"],
      transporter: getTransporterTSICode(item["ambPEC"]),
      transporterName: item["ambPECName"],
      transportType: item["transportType"],
      departureHC: departureHc,
      arrivalHC: arrivalHC,
      departure: dep,
      arrival: arr,
      patientBirthname: item["patientBirthname"],
      patientBirthdate: parseDateString(item["patientBirthdate"]),
      orderedLeaveDate: parseDateAndHoursString(item["leaveDate"]),
      orderedArrivalDate: parseDateAndHoursString(item["arrivalDate"]),
      realStartDate: parseDateAndHoursString(item["startDate"]),
      realEndDate: parseDateAndHoursString(item["endDate"]),
      patientFirstname: item["patientFirstname"],
      patientLastname: item["patientName"],
      patientSex: item["patientSex"],
      toll: item["peage"] && item["peage"] !== "0" ? item["peage"] : undefined,
    };
    Object.keys(fare).forEach((key) =>
      (fare as any)[key] === undefined ? delete (fare as any)[key] : {}
    );

    fares.push(fare);
  }
  return fares;
}

export async function parseFile(file: File): Promise<Fare[]> {
  const headers = [
    ["IDMission", "identifier"],
    ["Ref du centre", "orderer"],
    ["N° Contrat", "contract"],
    ["Service", "service"],
    ["Date départ théorique", "leaveDate"],
    ["Date Arrivée théorique", "arrivalDate"],
    ["Type véhicule", "transportType"],
    ["Mission", "missionType"],
    ["Etat", "status"],
    ["Niveau", "level"],
    ["Urgent", "priority"],
    ["Etablissement arrivée", "arrivalHealthCenter"],
    ["Etablissement départ", "departureHealthCenter"],
    ["Nom patient", "patientName"],
    ["Prénom patient", "patientFirstname"],
    ["Nom JF Patient", "patientBirthname"],
    ["Date naissance", "patientBirthdate"],
    ["Sexe", "patientSex"],
    ["Prescripteur", "prescripteur"],
    ["AmbPatient", "patientChoice"],
    ["Amb PEC", "ambPEC"],
    ["Lib Amb PEC", "ambPECName"],
    ["Type PEC", "PECType"],
    ["Lieu départ", "departurePlace"],
    ["Chbre départ", "departureRoom"],
    ["Adresse départ", "departureAddress-unused"],
    ["Adresse1 départ", "departureAddress"],
    ["Adresse2 départ", "departureAddress2"],
    ["CP départ", "departureZip"],
    ["Ville départ", "departureCity"],
    ["INSEE départ", "departureINSEE"],
    ["Lieu arrivée", "arrivalPlace"],
    ["Adresse1 arrivée", "arrivalAddress"],
    ["Adresse2 arrivée", "arrivalAddress2"],
    ["CP arrivée", "arrivalZip"],
    ["Ville arrivée", "arrivalCity"],
    ["INSEE arrivée", "arrivalINSEE"],
    ["Distance", "distance"],
    ["Service départ", "departureService"],
    ["Chambre arrivée", "arrivalRoom"],
    ["Service arrivée", "arrivalService"],
    ["IPP", "IPP"],
    ["Payeur", "payer"],
    ["Péage", "toll"],
    ["UF Départ", "departureUF"],
    ["UF arrivée", "arrivalUF"],
    ["HorairePEC", "startDate"],
    ["HoraireDEB", "debDate"],
    ["HoraireARR", "endDate"],
  ];

  return new Promise<Fare[]>((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      transformHeader: (original: string) => {
        const replacement = headers.find((h) => h[0] === original);
        if (replacement) {
          return replacement[1];
        }
        return original;
      },
      complete: (parsed) => {
        if (parsed.data) {
          const fares = mapCSVToFares(parsed.data);
          resolve(fares);
        } else {
          reject();
        }
      },
    });
  });
}
